import clsx from "clsx";
import { House, LayoutGrid, Search, SquarePen, X } from "lucide-react";
import { useEffect, useState } from "react";

import { useCircuits } from "@/hooks/useCircuits";
import { useMoriartyEnvironments } from "@/hooks/useMoriarty";
import { useSessions } from "@/hooks/useSessions";
import {
  APP_MENU_ITEMS,
  isMenuSidebarView,
  resolveMenuSectionFromView,
} from "@/lib/menu-navigation";
import { clearReviewRouteParams } from "@/lib/review-navigation";
import { useStore } from "@/lib/store";
import type { Session } from "@/lib/types";

import { AnimatedThemeToggleIcon } from "./AnimatedThemeToggleButton";
import { SidebarAdvancedFiltersTrigger, SidebarMineToggle } from "./SidebarFilters";
import { SidebarFooter } from "./SidebarFooter";
import { SidebarSessionList } from "./SidebarSessionList";
import { Tooltip } from "./Tooltip";

type MobileTab = "sherlog" | "more";

function withDemoSessionDates(sessions: Session[]): Session[] {
  if (!import.meta.env.DEV) {
    return sessions;
  }

  const dayOffsets = [0, 0, 0, 1, 1, 2, 3, 5, 7, 10, 14];

  return sessions.map((session, index) => {
    const offsetDays = dayOffsets[index] ?? Math.min(21, dayOffsets[dayOffsets.length - 1] + index);
    const date = new Date();
    date.setHours(16 - (index % 5), 30 - (index % 3) * 10, 0, 0);
    date.setDate(date.getDate() - offsetDays);

    return {
      ...session,
      lastActivity: date.toISOString(),
    };
  });
}

export function Sidebar(): React.JSX.Element {
  const filters = useStore((state) => state.filters);
  const setFilter = useStore((state) => state.setFilter);
  const theme = useStore((state) => state.theme);
  const toggleTheme = useStore((state) => state.toggleTheme);
  const sidebarOpen = useStore((state) => state.sidebarOpen);
  const closeSidebar = useStore((state) => state.closeSidebar);
  const activeView = useStore((state) => state.activeView);
  const setView = useStore((state) => state.setView);
  const user = useStore((state) => state.user);
  const isAdmin = user?.role === "admin";
  const { data, isLoading } = useSessions(filters);
  const { data: allData } = useSessions(
    { ...filters, mine: undefined },
    { enabled: !!filters.mine },
  );
  const allCount = filters.mine ? allData?.total : data?.total;
  const { data: moriartyData } = useMoriartyEnvironments();
  const moriartyActiveCount = moriartyData?.total ?? 0;
  const { data: circuitsData } = useCircuits("personal", "enabled");
  const activeCircuitCount = circuitsData?.total ?? 0;
  const [mobileTab, setMobileTab] = useState<MobileTab>("sherlog");
  const [isSearchPinned, setIsSearchPinned] = useState(false);
  const [isThemeToggleHovered, setIsThemeToggleHovered] = useState(false);

  const menuSidebarMode = isMenuSidebarView(activeView);
  const activeMenuSection = resolveMenuSectionFromView(activeView);

  useEffect(() => {
    if (menuSidebarMode) {
      setIsSearchPinned(false);
    }
  }, [menuSidebarMode]);

  // Auto-select the correct mobile tab when the sidebar opens
  useEffect(() => {
    if (!sidebarOpen) return;
    setMobileTab(menuSidebarMode ? "more" : "sherlog");
  }, [sidebarOpen, menuSidebarMode]);

  function setViewQueryParam(
    view:
      | "stats"
      | "moriarty"
      | "circuits"
      | "github-reviews"
      | "docs"
      | "download"
      | "preferences"
      | "admin"
      | null,
  ): void {
    const url = new URL(window.location.href);
    clearReviewRouteParams(url);
    url.searchParams.delete("thread");
    url.searchParams.delete("moriarty");
    url.searchParams.delete("doc");
    if (view) {
      url.searchParams.set("view", view);
    } else {
      url.searchParams.delete("view");
    }
    window.history.pushState({}, "", url);
  }

  function handleBackToSherlog(): void {
    setView("sessions");
    setViewQueryParam(null);
    closeSidebar();
  }

  function handleMenuNavigation(
    key: (typeof APP_MENU_ITEMS)[number]["key"],
    queryParam: (typeof APP_MENU_ITEMS)[number]["queryParam"],
  ): void {
    setView(key);
    setViewQueryParam(queryParam);
    closeSidebar();
  }

  function getMenuBadge(key: (typeof APP_MENU_ITEMS)[number]["key"]): number | null {
    switch (key) {
      case "moriarty":
        return moriartyActiveCount > 0 ? moriartyActiveCount : null;
      case "circuits":
        return activeCircuitCount > 0 ? activeCircuitCount : null;
      default:
        return null;
    }
  }

  const menuItems = APP_MENU_ITEMS.filter((item) => (item.requiresAdmin ? isAdmin : true)).map(
    (item) => ({
      ...item,
      badge: getMenuBadge(item.key),
      isActive: activeMenuSection === item.key,
      onClick: () => handleMenuNavigation(item.key, item.queryParam),
    }),
  );
  const actionSurfaceClass =
    theme === "base"
      ? "bg-cyber-border/70 hover:bg-cyber-border"
      : "bg-cyber-border/60 hover:bg-cyber-border/80";
  const sidebarHoverSurfaceClass =
    theme === "base" ? "hover:bg-cyber-border/80" : "hover:bg-cyber-border/50";
  const actionFocusClass =
    "focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyber-cyan/35";
  const actionIconClass = "text-cyber-text/75";
  const visibleSessions = withDemoSessionDates(data?.sessions ?? []);

  function getToneClasses(tone: "cyan" | "magenta"): {
    active: string;
    hover: string;
    icon: string;
    activeBg: string;
  } {
    if (tone === "magenta") {
      return {
        active: "text-cyber-magenta",
        hover: "hover:text-cyber-magenta",
        icon: "group-hover:text-cyber-magenta",
        activeBg: "bg-cyber-magenta/10",
      };
    }

    return {
      active: "text-cyber-cyan",
      hover: "hover:text-cyber-cyan",
      icon: "group-hover:text-cyber-cyan",
      activeBg: "bg-cyber-cyan/10",
    };
  }

  function renderRailIconButton({
    title,
    onClick,
    icon,
    active = false,
    tone = "cyan",
    badge,
    tooltip = true,
    hoverClass,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
  }: {
    title: string;
    onClick: () => void;
    icon: React.JSX.Element;
    active?: boolean;
    tone?: "cyan" | "magenta";
    badge?: number | null;
    tooltip?: boolean;
    hoverClass?: string;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onFocus?: () => void;
    onBlur?: () => void;
  }): React.JSX.Element {
    const toneClasses = getToneClasses(tone);
    const button = (
      <button
        onClick={onClick}
        className={clsx(
          "group relative rounded-xl p-2 transition-all before:absolute before:-inset-1 before:rounded-[inherit] before:content-['']",
          active
            ? clsx(toneClasses.activeBg, toneClasses.active)
            : clsx("text-cyber-muted", sidebarHoverSurfaceClass, hoverClass ?? toneClasses.hover),
        )}
        aria-label={title}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        {icon}
        {typeof badge === "number" && badge > 0 ? (
          <span className="bg-cyber-surface text-cyber-text absolute top-0.5 right-0.5 min-w-[14px] rounded-full px-1 text-center text-[9px] leading-[14px] font-medium">
            {badge > 99 ? "99+" : badge}
          </span>
        ) : null}
      </button>
    );

    if (!tooltip) {
      return button;
    }

    return <Tooltip content={title}>{button}</Tooltip>;
  }

  function renderDesktopRail(): React.JSX.Element {
    const sherlogItem = menuItems.find((item) => item.key === "sessions");
    const topMenuItems = menuItems.filter(
      (item) => item.key !== "sessions" && item.key !== "preferences",
    );
    const settingsItem = menuItems.find((item) => item.key === "preferences");

    return (
      <div className="border-cyber-border hidden h-full w-[60px] shrink-0 flex-col items-center gap-3 border-r py-3 md:flex">
        {sherlogItem
          ? renderRailIconButton({
              title: sherlogItem.label,
              onClick: sherlogItem.onClick,
              icon: <sherlogItem.icon size={20} />,
              active: sherlogItem.isActive,
              tone: sherlogItem.tone,
              badge: sherlogItem.badge,
            })
          : null}

        <div className="bg-cyber-border/60 h-px w-6" aria-hidden />

        {topMenuItems.map((item) => (
          <div key={item.key}>
            {renderRailIconButton({
              title: item.label,
              onClick: item.onClick,
              icon: <item.icon size={20} />,
              active: item.isActive,
              tone: item.tone,
              badge: item.badge,
            })}
          </div>
        ))}

        <div className="bg-cyber-border/60 h-px w-6" aria-hidden />

        {settingsItem
          ? renderRailIconButton({
              title: settingsItem.label,
              onClick: settingsItem.onClick,
              icon: <settingsItem.icon size={20} />,
              active: settingsItem.isActive,
              tone: settingsItem.tone,
              badge: settingsItem.badge,
            })
          : null}

        <div className="mt-auto flex flex-col items-center gap-2">
          {renderRailIconButton({
            title: theme === "cyber" ? "Switch to light theme" : "Switch to cyber theme",
            onClick: toggleTheme,
            icon: (
              <AnimatedThemeToggleIcon
                checked={theme === "cyber"}
                hovered={isThemeToggleHovered}
                className="size-5"
              />
            ),
            tooltip: false,
            hoverClass: theme === "cyber" ? "hover:text-cyber-yellow" : "hover:text-cyber-cyan",
            onMouseEnter: () => setIsThemeToggleHovered(true),
            onMouseLeave: () => setIsThemeToggleHovered(false),
            onFocus: () => setIsThemeToggleHovered(true),
            onBlur: () => setIsThemeToggleHovered(false),
          })}

          <SidebarFooter collapsed />
        </div>
      </div>
    );
  }

  function renderDesktopSessionContent(): React.JSX.Element {
    return (
      <div
        className="hidden min-h-0 w-[280px] min-w-0 flex-1 flex-col overflow-y-auto md:flex"
        onScroll={(event) => {
          const nextPinned = event.currentTarget.scrollTop > 0;
          setIsSearchPinned((current) => (current === nextPinned ? current : nextPinned));
        }}
      >
        <div
          className={clsx(
            "bg-cyber-bg/95 sticky top-0 isolate z-20 backdrop-blur",
            isSearchPinned ? "border-cyber-border border-b" : "",
          )}
        >
          <div className="px-3 pt-5 pb-5">
            <div className="flex items-center gap-2">
              {renderSearchField()}
              {renderNewChatButton()}
            </div>
          </div>
        </div>

        <SidebarSessionList
          sessions={visibleSessions}
          isLoading={isLoading}
          headerAction={
            <div className="flex items-center gap-2">
              <div className="hidden">
                <SidebarMineToggle allCount={allCount} />
              </div>
              <SidebarAdvancedFiltersTrigger allCount={allCount} />
            </div>
          }
        />
      </div>
    );
  }

  function renderSearchField(heightClass = "h-8"): React.JSX.Element {
    return (
      <div className="relative min-w-0 flex-1">
        <Search
          size={14}
          className={clsx("absolute top-1/2 left-3 -translate-y-1/2", actionIconClass)}
        />
        <input
          type="text"
          value={filters.search || ""}
          onChange={(event) => setFilter("search", event.target.value || undefined)}
          placeholder="Search chats"
          className={clsx(
            "font-ui text-cyber-text placeholder:text-cyber-muted/65 w-full rounded-full border border-transparent pr-9 pl-10 text-[12px] transition-all focus:outline-none",
            heightClass,
            actionSurfaceClass,
            actionFocusClass,
          )}
        />
        {filters.search && (
          <button
            type="button"
            onClick={() => setFilter("search", undefined)}
            className={clsx(
              "text-cyber-muted hover:text-cyber-text absolute top-1/2 right-2 inline-flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full transition-all before:absolute before:-inset-1 before:rounded-full before:content-['']",
              sidebarHoverSurfaceClass,
            )}
            title="Clear search"
            aria-label="Clear search"
          >
            <X size={12} />
          </button>
        )}
      </div>
    );
  }

  function renderNewChatButton(): React.JSX.Element {
    return (
      <button
        onClick={handleBackToSherlog}
        className={clsx(
          "hover:text-cyber-cyan relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-transparent transition-all before:absolute before:-inset-1 before:rounded-full before:content-['']",
          actionSurfaceClass,
          actionFocusClass,
          actionIconClass,
        )}
        title="New Chat"
        aria-label="New Chat"
      >
        <SquarePen size={14} strokeWidth={1.8} />
      </button>
    );
  }

  function renderNavListItem({
    icon: Icon,
    label,
    tone = "cyan",
    active = false,
    badge,
    onClick,
    trailing,
  }: {
    icon: (typeof menuItems)[number]["icon"];
    label: string;
    tone?: "cyan" | "magenta";
    active?: boolean;
    badge?: number | null;
    onClick: () => void;
    trailing?: React.JSX.Element | null;
  }): React.JSX.Element {
    const toneClasses = getToneClasses(tone);

    return (
      <button
        onClick={onClick}
        className={clsx(
          "group flex h-9 w-full items-center gap-3 rounded-xl px-3 text-left text-sm transition-all",
          active
            ? clsx(
                theme === "base" ? "bg-cyber-border/70" : "bg-cyber-border/60",
                toneClasses.active,
              )
            : clsx(
                "text-cyber-muted",
                theme === "base"
                  ? "hover:bg-cyber-border/50 hover:text-cyber-text"
                  : "hover:bg-cyber-border/40 hover:text-cyber-text",
                toneClasses.hover,
              ),
        )}
      >
        <Icon
          size={16}
          className={clsx(
            "shrink-0 transition-all",
            active ? toneClasses.active : toneClasses.icon,
          )}
        />
        <span className="font-ui text-[14px] leading-5">{label}</span>
        {trailing ??
          (badge ? (
            <span className="bg-cyber-surface text-cyber-muted ml-auto rounded-full px-1.5 py-0.5 text-[10px] leading-5">
              {badge}
            </span>
          ) : null)}
      </button>
    );
  }

  function renderSecondaryMenuList({
    includeSherlog = false,
  }: {
    includeSherlog?: boolean;
  } = {}): React.JSX.Element {
    return (
      <div className="space-y-1">
        {menuItems
          .filter((item) => includeSherlog || item.key !== "sessions")
          .map((item) => (
            <div key={item.key}>
              {renderNavListItem({
                icon: item.icon,
                label: item.label,
                tone: item.tone,
                active: item.isActive,
                badge: item.badge,
                onClick: item.onClick,
              })}
            </div>
          ))}
      </div>
    );
  }

  function renderMobileTabBar(): React.JSX.Element {
    const tabClass = (active: boolean) =>
      clsx(
        "relative inline-flex h-[60px] flex-1 items-center justify-center gap-2 border-b-2 text-[12px] font-medium transition-all",
        active
          ? "border-cyber-cyan text-cyber-text bg-transparent"
          : clsx(
              "text-cyber-text/70 hover:bg-cyber-surface/20 hover:text-cyber-text border-transparent bg-transparent",
            ),
      );

    return (
      <div className="border-cyber-border flex border-b">
        <button
          type="button"
          className={tabClass(mobileTab === "sherlog")}
          onClick={() => setMobileTab("sherlog")}
        >
          <House size={14} className="shrink-0" />
          <span className="font-ui leading-5">Sherlog</span>
        </button>
        <button
          type="button"
          className={tabClass(mobileTab === "more")}
          onClick={() => setMobileTab("more")}
        >
          <LayoutGrid size={14} className="shrink-0" />
          <span className="font-ui leading-5">More</span>
        </button>
      </div>
    );
  }

  function renderMobileContent(): React.JSX.Element {
    return (
      <div className="flex min-h-full w-[280px] flex-col md:hidden">
        <div className="border-cyber-border bg-cyber-bg/95 border-b backdrop-blur">
          <div>{renderMobileTabBar()}</div>
        </div>

        {mobileTab === "sherlog" ? (
          <>
            <div className="px-3 pt-3 pb-3">
              <div className="flex items-center gap-2">
                {renderSearchField("h-10")}
                {renderNewChatButton()}
              </div>
            </div>

            <SidebarSessionList
              sessions={visibleSessions}
              isLoading={isLoading}
              headerAction={
                <div className="flex items-center gap-2">
                  <div className="hidden">
                    <SidebarMineToggle allCount={allCount} />
                  </div>
                  <SidebarAdvancedFiltersTrigger allCount={allCount} />
                </div>
              }
            />
          </>
        ) : (
          <div className="flex-1 overflow-y-auto px-3 pt-3">{renderSecondaryMenuList()}</div>
        )}

        <div className="border-cyber-border bg-cyber-bg/95 sticky bottom-0 mt-auto border-t px-3 py-2 backdrop-blur">
          <SidebarFooter className="px-0 py-0" />
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        aria-label="Close sidebar"
        className={clsx("sidebar-backdrop md:hidden", sidebarOpen && "open")}
        onClick={closeSidebar}
      />

      <aside
        className={clsx(
          "sidebar border-cyber-border bg-cyber-bg border-r",
          sidebarOpen && "open",
          menuSidebarMode && "menu-mode",
        )}
      >
        <div className="hidden h-full md:flex">
          {renderDesktopRail()}
          {!menuSidebarMode ? renderDesktopSessionContent() : null}
        </div>
        {renderMobileContent()}
      </aside>
    </>
  );
}
